// ゲームの登録簿。**ゲームを1本足すときに触るのはここだけ**にする。
//
// 載せる情報は3つ:
//   1. 設定スライス — boot 時にサーバー設定と往復させるので**静的**に持つ（数KB。Phaser は含まない）
//   2. 本体の読み込み関数 — Phaser とシーン一式なので**動的**（遊ぶと決まってから落とす）
//   3. カードの見た目 — ダッシュボードに1枚出すためのカバー絵とバッジ（静的・数百バイト）
//
// 1と2の非対称がこのファイルの要点。設定は軽いから常に持ち、本体は重いから遅らせる。
// 3を**ダッシュボード側ではなくここに置く**のは、「足すときに触るのはここだけ」を守るため。
// 文言（名前・説明）は持たない ＝ `portal.game.<key>.name` / `.desc` をカタログから引く
// （足し忘れは `__tests__/registry.test.ts` が検出する）。
import type Phaser from 'phaser';
import type { GameSettingsSlice } from '@/platform/net/settings';
import type { StageRecord } from '@/platform/stageRecord';
import { GAME_KEY as NIKAKUDORI } from './nikakudori/gameKey';
import { nikakudoriSettingsSlice } from './nikakudori/net/gameSettings';
import { GAME_KEY as TRIANGLE_SWEEPER } from './triangle-sweeper/gameKey';
import { triangleSweeperSettingsSlice } from './triangle-sweeper/net/gameSettings';
import { syncClaimedShapes } from './triangle-sweeper/net/unlock';
import { GAME_KEY as CARDS } from './cards/gameKey';
import { cardsSettingsSlice } from './cards/net/gameSettings';
import { GAME_KEY as G2048 } from './2048/gameKey';
import { game2048SettingsSlice } from './2048/net/gameSettings';
import { GAME_KEY as CRATES } from './crates/gameKey';
import { cratesSettingsSlice } from './crates/net/gameSettings';
import { GAME_KEY as SLIDE } from './slide/gameKey';
import { slideSettingsSlice } from './slide/net/gameSettings';
import { GAME_KEY as NONOGRAM } from './nonogram/gameKey';
import { nonogramSettingsSlice } from './nonogram/net/gameSettings';
import { GAME_KEY as NANPURE } from './nanpure/gameKey';
import { nanpureSettingsSlice } from './nanpure/net/gameSettings';
import { GAME_KEY as COLORSORT } from './colorsort/gameKey';
import { colorsortSettingsSlice } from './colorsort/net/gameSettings';
import { GAME_KEY as SAMEGAME } from './samegame/gameKey';
import { samegameSettingsSlice } from './samegame/net/gameSettings';
import { GAME_KEY as MAHJONG } from './mahjong/gameKey';
import { kakuroSettingsSlice } from './kakuro/net/gameSettings';
import { GAME_KEY as KAKURO } from './kakuro/gameKey';
import { mahjongSettingsSlice } from './mahjong/net/gameSettings';
import { GAME_KEY as NUMBERSUM } from './numbersum/gameKey';
import { numbersumSettingsSlice } from './numbersum/net/gameSettings';
import { GAME_KEY as PIPES } from './pipes/gameKey';
import { pipesSettingsSlice } from './pipes/net/gameSettings';

/** 動的 import で落ちてくるゲーム本体が満たすべき形。 */
export interface GameRuntime {
  createGame(opts: { parent: string; onReady: () => void }): Phaser.Game;
  /**
   * ホームから**同じゲームへ**戻ってきたときに開き直すシーンのキー。
   *
   * `main.ts` が `'Title'` を直書きしていると、その名前のシーンを持たないゲームで
   * `Scene key not found` になる（＝platform がゲームの中身を仮定してしまっている）。
   * **各ゲームが自分で名乗る**ことで platform 側は名前を知らないままでいられるし、
   * 付け忘れは TS が止める。
   */
  readonly ENTRY_SCENE_KEY: string;
}

/**
 * 小さなコマに1文字。牌に見立てた白いコマに置く。`ink` は文字色（既定は赤＝萬子の色）。
 *
 * ⚠️ **カードの絵ではなくなった**（2026-08-22）。いまの用途は2つだけ:
 *   人気ゲームランキングの行頭アイコン（先頭の1文字だけ使う）と、近日公開カードの絵。
 *   遊べるゲームのカードには `logo` の画像が出る。
 */
export interface CoverTile {
  readonly char: string;
  readonly ink?: 'green' | 'black';
}

/**
 * ダッシュボードのカードに出す見た目。
 */
export interface GameCard {
  /**
   * カードに出すタイトルロゴ（`public/` からのパス）。
   *
   * 🔴 **ゲーム本体の素材を直接指さない**。素材は 900〜1080px 幅で、6本ぶんを
   *   ダッシュボードが読むと**初回表示に 1.1MB 増える**（このポータルは初期JS gzip 12kB で
   *   組んである＝そこへ画像を素で足すと桁が変わる）。⇒ `assets/portal/covers/` に
   *   **カード用に縮めた複製**を置く。作り方は [docs/PORTAL-COVERS.md](../../docs/PORTAL-COVERS.md)。
   * ⚠️ 複製なので、ゲーム側のロゴを描き直したら**ここも焼き直す**こと
   *   （素材の複製は参照ではない、という [games/<key>/config/sounds.ts] と同じ約束）。
   */
  readonly logo: string;
  /** ランキング行のアイコンと、近日公開カードの絵に使う（`CoverTile` の注記を参照）。 */
  readonly cover: readonly CoverTile[];
  /** ★ランキング対応バッジを出すか（スコアを D1 へ送るゲームだけ true）。 */
  readonly ranked: boolean;
}

/**
 * ゲスト記録をアカウントへ持ち込むための「この端末の記録」
 * （統合＝docs/AUTH.md の実装順序⑤ / [platform/net/merge.ts](@/platform/net/merge)）。
 *
 * **サーバーの表の粒度そのまま**にしてある＝ platform 側がゲームの区切り方を知らずに済む。
 */
export interface GameMergeContribution {
  /** `player_stage_progress` の行（ステージ番号 → そのゲームの記録の並び）。 */
  readonly progress: readonly { stageNo: number; parts: readonly StageRecord[] }[];
  /** `scores` の行（ランキングへ反映する自己ベスト）。未クリアの枠は含めないこと。 */
  readonly scores: readonly { stageKey: string; score: number; stars: number }[];
  /**
   * 持ち込む記録が付いた**物差しの版**（マイグレーション 0022）。
   * `RECORDS_VERSION` を持つゲームだけが送る（持たないゲームは省略＝サーバー側は `null`）。
   *
   * ⚠️ **既にサーバーにある行の版は書き換わらない**（[auth/merge.ts](../../functions/api/auth/merge.ts) の 🔴）
   *   ——統合後の `parts` にはサーバー側の記録が混ざるので、現行の版を刻むと
   *   古い物差しの点が「現行」の顔になる。ここが効くのは**新しく作る行**だけ。
   */
  readonly recordsVersion?: number;
}

export interface GameEntry {
  /** `game_key`。ディレクトリ名・カタログ名・アセット名・localStorage 接頭辞と同じ文字列。 */
  readonly key: string;
  /** 設定の読み書き（`/api/settings` の `games.<key>` として往復する）。 */
  readonly settings: GameSettingsSlice;
  /** Phaser とシーン一式を遅延読込する。 */
  readonly load: () => Promise<GameRuntime>;
  /** ダッシュボードのカード。 */
  readonly card: GameCard;
  /**
   * **完走ライン**＝「そのゲームを1単位ぶん遊んだ」と言える★の数。
   * メダルのレートの分母（[docs/CURRENCY.md](../../docs/CURRENCY.md) 決定8・2026-09-11）。
   *
   * 🔴 **★上限（枠の数 × 3）とは限らない**。★上限は「分量」ではなく
   *   **ランキングの刻み方**を測っているので、⚠️ ステージがそのまま枠になる本
   *   （箱押し400面・ノノグラム500面）は★上限が 1200 / 1500 になり、
   *   **粗く報告する本（カックロは300面の中身で枠10個）が桁違いに得をする**。
   *   ⇒ ステージ直列型は **1グループ（`GROUP_SIZE` = 10面）＝ ★30** を1単位にする。
   *
   * ⚠️ **ここは数字の写し**で、根拠は各ゲームの定数側にある。
   *   [__tests__/completionStars.test.ts](__tests__/completionStars.test.ts) が
   *   **毎回計算し直して突き合わせる**ので、難易度を1段足したら落ちる。
   */
  readonly completionStars: number;
  /**
   * 起動時にサーバーのステージ進捗と突き合わせる（登録プレイヤーのキャッシュを温める）。
   * **動的 import**＝設定スライスと違い、これは**登録プレイヤーしか使わない**ので
   * ゲストに1バイトも払わせない（呼び出し側が `isRegistered()` で門を閉める）。
   * 進捗の概念を持たないゲームは省略できる。
   */
  readonly reconcileProgress?: () => Promise<void>;
  /**
   * ゲスト記録をアカウントへ持ち込むときに読む「この端末の記録」（統合）。
   * `reconcileProgress` と同じく**動的 import**＝統合はログイン時にしか走らないので、
   * ゲストにも普段のプレイにも1バイトも払わせない。
   *
   * 🔴 **`reconcileProgress` を持つゲームは、これも必ず持つこと**。
   *    サーバーに進捗があるということは、ログイン後に**ローカルがサーバーで置き換わる**
   *    ということで、ここが無いと**この端末の記録が黙って消える**。
   *    片方だけの状態は `__tests__/registry.test.ts` が検出する。
   */
  readonly collectMerge?: () => Promise<GameMergeContribution>;
}

/**
 * メダルの**基準総額**＝「完走ライン1本ぶん」で得られるメダル。
 * `star_rate_g = MEDAL_BASE_PER_GAME ÷ completionStars_g`（[docs/CURRENCY.md](../../docs/CURRENCY.md) 決定8）。
 *
 * 🔴 **540 は「レートが全ゲームで整数になる最小の値」**（完走ライン 9/12/18/30/54/135 の最小公倍数）。
 *   ⚠️ 小数にすると `SUM(stars * star_rate)` が浮動小数になり、
 *   **足す順で末尾が変わって同点の並びが揺れる**。整数なら順位が安定する。
 *   ⇒ 完走ラインを1つでも変えたら**ここも取り直す**
 *   （[__tests__/starRateMigration.test.ts](__tests__/starRateMigration.test.ts) が割り切れないと落ちる）。
 */
export const MEDAL_BASE_PER_GAME = 540;

export const GAMES: readonly GameEntry[] = [
  {
    key: NIKAKUDORI,
    completionStars: 54, // 3難易度 × 6サイズ = 18枠
    settings: nikakudoriSettingsSlice,
    load: () => import('./nikakudori/runtime'),
    card: {
      logo: 'assets/portal/covers/nikakudori.webp',
      cover: [
        { char: '🀇' },
        { char: '🀙', ink: 'green' },
        { char: '🀫', ink: 'black' },
        { char: '🀄' },
        { char: '🀅', ink: 'green' },
        { char: '🀐', ink: 'black' },
        { char: '🀋' },
        { char: '🀛', ink: 'green' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./nikakudori/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./nikakudori/net/merge').then((m) => m.collectMerge()),
  },
  {
    key: TRIANGLE_SWEEPER,
    completionStars: 135, // 15形状 × 3爆弾率 = 45枠
    settings: triangleSweeperSettingsSlice,
    load: () => import('./triangle-sweeper/runtime'),
    card: {
      logo: 'assets/portal/covers/triangle-sweeper.webp',
      // 赤＝辺で接する隣（重み +2）／緑＝頂点で接する隣（+1）＝**ゲーム内のガイド色と同じ**。
      // 数字と爆弾・旗を混ぜて「三角形のマインスイーパ」だと一目で分かるようにする。
      cover: [
        { char: '▲' },
        { char: '1', ink: 'black' },
        { char: '▼', ink: 'green' },
        { char: '💣', ink: 'black' },
        { char: '▲', ink: 'green' },
        { char: '3', ink: 'black' },
        { char: '▼' },
        { char: '🚩', ink: 'black' },
      ],
      ranked: true,
    },
    // サーバー進捗が届いた**直後**に解錠の引き継ぎも済ませる。新しい端末では
    // `createGame` の時点でまだ記録が空＝引き継げないので、ここが受け皿になる
    // （[triangle-sweeper/net/unlock.ts](./triangle-sweeper/net/unlock.ts) の 🔴 参照）。
    // ※ `syncClaimedShapes` は**静的 import で足りる**。`unlock` は上の `settings` 経由で
    //   どのみち初期バンドルに載っており、動的にしても分離されない（rolldown が警告する）。
    //   重い進捗モジュールのほうは従来どおり動的のまま。
    reconcileProgress: () =>
      import('./triangle-sweeper/net/progress').then(async (m) => {
        await m.reconcileProgressWithServer();
        syncClaimedShapes(m.getProgress());
      }),
    collectMerge: () => import('./triangle-sweeper/net/merge').then((m) => m.collectMerge()),
  },
  {
    key: CARDS,
    completionStars: 18, // 2めくり × 3難易度 = 6枠
    settings: cardsSettingsSlice,
    load: () => import('./cards/runtime'),
    card: {
      logo: 'assets/portal/covers/cards.webp',
      // 4スート＋ランクを混ぜて「トランプ」だと一目で分かるようにする。
      // ※ ここはダッシュボードの HTML 側なので、Phaser 内と違い絵文字を使ってよい
      //   （iOS の絵文字置換は「文字として描いた記号」の問題で、DOM では意図どおり）。
      cover: [
        { char: '🂡' },
        { char: '🂺', ink: 'black' },
        { char: '🃎' },
        { char: '🃓', ink: 'green' },
        { char: '🂭' },
        { char: '🂷', ink: 'black' },
        { char: '🃁' },
        { char: '🃚', ink: 'green' },
      ],
      // 2026-08-10 に `net/progress.ts` ＋ クリア時 `submitScore` を繋いだので `true`。
      // （公開時は「遊んだのにランキングに出ない」を避けるため `false` にしてあった）
      ranked: true,
    },
    reconcileProgress: () =>
      import('./cards/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./cards/net/merge').then((m) => m.collectMerge()),
  },
  {
    key: G2048,
    completionStars: 9, // 3サイズ = 3枠
    settings: game2048SettingsSlice,
    load: () => import('./2048/runtime'),
    card: {
      logo: 'assets/portal/covers/2048.webp',
      // 実際に出てくるタイルの数字を並べる。**ロゴと同じ色の並び**で、
      // 「数字を合わせて大きくする」ゲームだと一目で分かるようにする。
      cover: [
        { char: '2' },
        { char: '4', ink: 'black' },
        { char: '8', ink: 'green' },
        { char: '16' },
        { char: '32', ink: 'green' },
        { char: '64', ink: 'black' },
        { char: '128' },
        { char: '256', ink: 'green' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./2048/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./2048/net/merge').then((m) => m.collectMerge()),
  },
  {
    key: CRATES,
    completionStars: 30, // 🔴 1グループ=10面（全400面ではない）
    settings: cratesSettingsSlice,
    load: () => import('./crates/runtime'),
    card: {
      logo: 'assets/portal/covers/crates.webp',
      // 倉庫の一場面をそのまま並べる＝箱・壁・ゴールの印・作業員。
      // 「押して印の上へ運ぶ」ゲームだと一目で分かるようにする。
      cover: [
        { char: '📦' },
        { char: '🧱', ink: 'black' },
        { char: '📦' },
        { char: '🎯', ink: 'green' },
        { char: '🧱', ink: 'black' },
        { char: '📦' },
        { char: '🎯', ink: 'green' },
        { char: '👷' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./crates/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./crates/net/merge').then((m) => m.collectMerge()),
  },
  {
    key: SLIDE,
    completionStars: 9, // 3サイズ = 3枠
    settings: slideSettingsSlice,
    load: () => import('./slide/runtime'),
    card: {
      logo: 'assets/portal/covers/slide.webp',
      // ロゴと同じ「クリームの数字タイル」を並べる。**順番に並んでいる**ことで、
      // 「ばらばらの数字を順に並べ直すゲーム」だと一目で分かるようにする。
      cover: [
        { char: '1' },
        { char: '2', ink: 'black' },
        { char: '3', ink: 'green' },
        { char: '4' },
        { char: '5', ink: 'green' },
        { char: '6', ink: 'black' },
        { char: '7' },
        { char: '8', ink: 'green' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./slide/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./slide/net/merge').then((m) => m.collectMerge()),
  },

  {
    key: NONOGRAM,
    completionStars: 30, // 🔴 1グループ=10面（全500面ではない）
    settings: nonogramSettingsSlice,
    load: () => import('./nonogram/runtime'),
    card: {
      logo: 'assets/portal/covers/nonogram.webp',
      // 手掛かりの数字と、盤の2つの状態（塗り・×）を並べる。
      // 「数字を読んでマスを塗る」ゲームだと一目で分かるようにする。
      cover: [
        { char: '3' },
        { char: '1', ink: 'black' },
        { char: '■' },
        { char: '■' },
        { char: '×', ink: 'green' },
        { char: '■' },
        { char: '2', ink: 'black' },
        { char: '×', ink: 'green' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./nonogram/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./nonogram/net/merge').then((m) => m.collectMerge()),
  },

  {
    key: NANPURE,
    completionStars: 12, // 4難易度 = 4枠
    settings: nanpureSettingsSlice,
    load: () => import('./nanpure/runtime'),
    card: {
      logo: 'assets/portal/covers/nanpure.webp',
      // 盤の一列をそのまま並べる＝数字と、まだ埋まっていないマス（・）。
      // 「数字を1つずつ埋めていく」ゲームだと一目で分かるようにする。
      cover: [
        { char: '5' },
        { char: '3', ink: 'black' },
        { char: '・', ink: 'green' },
        { char: '7' },
        { char: '・', ink: 'green' },
        { char: '9', ink: 'black' },
        { char: '1' },
        { char: '・', ink: 'green' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./nanpure/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./nanpure/net/merge').then((m) => m.collectMerge()),
  },

  {
    key: COLORSORT,
    completionStars: 30, // 10段 = 10枠
    settings: colorsortSettingsSlice,
    load: () => import('./colorsort/runtime'),
    card: {
      logo: 'assets/portal/covers/colorsort.webp',
      // 試験管と色の並び。**ロゴと同じ色**で、「色を仕分けるゲーム」だと一目で分かるようにする。
      cover: [
        { char: '🧪' },
        { char: '🟥' },
        { char: '🟦' },
        { char: '🟨' },
        { char: '🟩' },
        { char: '🟪' },
        { char: '🟧' },
        { char: '🧪' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./colorsort/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./colorsort/net/merge').then((m) => m.collectMerge()),
  },

  {
    key: SAMEGAME,
    completionStars: 30, // 10段 = 10枠
    settings: samegameSettingsSlice,
    load: () => import('./samegame/runtime'),
    card: {
      logo: 'assets/portal/covers/samegame.webp',
      // 盤の一場面をそのまま並べる＝同じ色が隣り合っているところと、爆弾。
      // 「同じ色のかたまりを消す」ゲームだと一目で分かるようにする。
      cover: [
        { char: '🟥' },
        { char: '🟥' },
        { char: '🟩' },
        { char: '🟦' },
        { char: '🟦' },
        { char: '🟨' },
        { char: '💣', ink: 'black' },
        { char: '🟪' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./samegame/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./samegame/net/merge').then((m) => m.collectMerge()),
  },

  {
    key: MAHJONG,
    completionStars: 30, // 10段 = 10枠
    settings: mahjongSettingsSlice,
    load: () => import('./mahjong/runtime'),
    card: {
      logo: 'assets/portal/covers/mahjong.webp',
      // 人気ランキングの行頭と、絵だけで中身が分かるように。
      // ⚠️ **二角取りと並ぶので「積んである」ことが伝わる並べ方**にする。
      cover: [
        { char: '🀄', ink: 'black' },
        { char: '🀙' },
        { char: '🀇' },
        { char: '🀐' },
        { char: '🀄', ink: 'black' },
        { char: '🀙' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./mahjong/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./mahjong/net/merge').then((m) => m.collectMerge()),
  },

  {
    key: KAKURO,
    completionStars: 30, // 10段 = 10枠
    settings: kakuroSettingsSlice,
    load: () => import('./kakuro/runtime'),
    card: {
      logo: 'assets/portal/covers/kakuro.webp',
      // 🔴 **数字パズルであることが絵で分かるように**。⚠️ ナンプレと並ぶので、
      //   カックロの見分けは「**黒いマスに斜線と2つの和**」が付いていること。
      cover: [
        { char: '🔢' },
        { char: '➕' },
        { char: '9️⃣' },
        { char: '🔷' },
        { char: '4️⃣' },
        { char: '➗' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./kakuro/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./kakuro/net/merge').then((m) => m.collectMerge()),
  },

  {
    key: NUMBERSUM,
    completionStars: 30, // 10段 = 10枠
    settings: numbersumSettingsSlice,
    load: () => import('./numbersum/runtime'),
    card: {
      logo: 'assets/portal/covers/numbersum.webp',
      // 🔴 **足し算のパズルであることが絵で分かるように**。⚠️ ナンプレ・カックロと並ぶので、
      //   見分けは「**数字が消えて合計に合う**」＝ 等号と合計の記号を入れてある。
      cover: [
        { char: '5️⃣' },
        { char: '➕' },
        { char: '3️⃣' },
        { char: '🟰' },
        { char: '8️⃣' },
        { char: '🧮' },
      ],
      ranked: true,
    },
    reconcileProgress: () =>
      import('./numbersum/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./numbersum/net/merge').then((m) => m.collectMerge()),
  },

  {
    key: PIPES,
    completionStars: 30, // 10段 = 10枠
    settings: pipesSettingsSlice,
    load: () => import('./pipes/runtime'),
    card: {
      logo: 'assets/portal/covers/pipes.webp',
      // 🔴 **管と水であることが絵で分かるように**。⚠️ 二角取り（経路探索）と並ぶので、
      //   見分けは「**水と蛇口**」＝ つなぐのが線ではなく配管であること。
      cover: [
        { char: '🚰' },
        { char: '🔧' },
        { char: '💧' },
        { char: '🟦' },
        { char: '🔩' },
        { char: '🚿' },
      ],
      // ✅ 2026-10-02 にランキングへつないだ（`TIME_REF_MS` を実測で置き直し、
      //   `TIME_REF_PROVISIONAL` を倒した・[game/score.ts](./pipes/game/score.ts)）。
      ranked: true,
    },
    reconcileProgress: () =>
      import('./pipes/net/progress').then((m) => m.reconcileProgressWithServer()),
    collectMerge: () => import('./pipes/net/merge').then((m) => m.collectMerge()),
  },
];

/**
 * 「近日公開」の枠。**まだ `src/games/<key>/` も D1 の行も無い**ので `GameEntry` にはならないが、
 * カードは出すので同じ表に並べておく（ダッシュボードが2箇所を見に行かなくて済む）。
 * 実装したら `GAMES` へ移し、ここから消す。
 */
export interface UpcomingEntry {
  /** カタログのキーにだけ使う（`portal.game.<key>.name` / `.desc`）。 */
  readonly key: string;
  readonly cover: readonly CoverTile[];
}

/**
 * 🔴 **今は空**（2026-09-11・ユーザー判断で `pair`（ペアめくり）を撤回）。
 *   理由は**ゲーム性**——神経衰弱と同等なら「記憶ゲーム」であって、
 *   このポータルが並べている**パズル**ではない。
 *   ⚠️ **空なのは書き忘れではない**。予告を1本も出していない状態が正しい。
 *
 * ⚠️ 当時（[docs/SPEC-kakuro.md](../../docs/SPEC-kakuro.md)）は
 *   「**予告カードを消すと『近日公開』と書いてあったものが黙って消えたことになる**」として
 *   残す判断をしていた。⇒ **作らないと決めたものを予告し続ける方が嘘になる**ので取り下げた。
 *
 * 💡 **空でもカードの段は壊れない**。`portal.section.gamesHint` は 0 本のとき
 *   「0 近日公開」と書かずに済むよう、`dashboard.ts` が文言を出し分ける。
 */
export const UPCOMING: readonly UpcomingEntry[] = [];

/** 未知のキーなら undefined（呼び出し側が既定へ落とす）。 */
export function findGame(key: string): GameEntry | undefined {
  return GAMES.find((g) => g.key === key);
}

/** 遊べるゲームのキー一覧。 */
export const GAME_KEYS: readonly string[] = GAMES.map((g) => g.key);

/**
 * 各ゲームが localStorage で使う接頭辞（`<key>.`）。
 * **データ削除（`clearLocalPlayerData`）が全ゲーム分を掃くのに使う**。
 * 手で書き足す方式だと**書き足し忘れがそのままプライバシーポリシーの嘘になる**ので、
 * 登録した時点で消える形にしてある。
 */
export const GAME_STORAGE_PREFIXES: readonly string[] = GAMES.map((g) => `${g.key}.`);

/**
 * ポータルの既定のゲーム（＝ダッシュボード先頭のカード、「ゲストで遊ぶ」の飛び先）。
 * **横断コードが特定のキーを直書きしないための唯一の出口**にする。
 */
export const DEFAULT_GAME: GameEntry = GAMES[0];
